import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { MotionConfig } from 'motion/react';

import I18nProvider from '@/contexts/i18n/i18n.provider';
import SnackbarProvider from '@/contexts/Snackbar/Snackbar';
import SessionProvider from '@/contexts/Session/Session';
import DevtoolsProvider from '@/contexts/Devtools/Devtools';
import { StatusProvider } from '@/contexts/Status/StatusProvider';
import { FullscreenLoadingProvider } from '@/contexts/FullscreenLoading/FullscreenLoadingProvider';
import { RequestFeedbackBridge } from '@/components/RequestFeedbackBridge';
import { BuildRecoveryGate } from '@/components/BuildRecoveryGate';

import { msalInstance } from '@/config/msalInstance';
import { env } from '@/config/env.config';
import { queryClient } from '@/queryClient';
import { isDevtoolsEnvironment } from '@/utils/env.utils';

const ReactQueryDevtools = lazy(async () => {
  const module = await import('@tanstack/react-query-devtools');
  return { default: module.ReactQueryDevtools };
});

interface IProvidersProps {
  children: ReactNode;
}

/**
 * The MSAL instance is imported from @/config/msalInstance (singleton).
 */

// msalInstance.enableAccountStorageEvents();
// msalInstance.addEventCallback((message: EventMessage) => {
//   if (message.eventType === EventType.LOGOUT_START) {
//     // Update UI with new account
//     // eslint-disable-next-line no-debugger
//     debugger;
//   }
// });

function RootProviders({ children }: IProvidersProps) {
  const devtoolsEnabled = isDevtoolsEnvironment(env.VITE_ENVIRONMENT);

  return (
    <MsalProvider instance={msalInstance}>
      <MotionConfig reducedMotion="user">
        <I18nProvider>
          <BuildRecoveryGate>
            <SessionProvider>
              <SnackbarProvider>
                <RequestFeedbackBridge />
                <QueryClientProvider client={queryClient}>
                  <StatusProvider>
                    <DevtoolsProvider>
                      <FullscreenLoadingProvider>
                        {children}
                      </FullscreenLoadingProvider>
                    </DevtoolsProvider>
                    {devtoolsEnabled && (
                      <Suspense fallback={null}>
                        <ReactQueryDevtools />
                      </Suspense>
                    )}
                  </StatusProvider>
                </QueryClientProvider>
              </SnackbarProvider>
            </SessionProvider>
          </BuildRecoveryGate>
        </I18nProvider>
      </MotionConfig>
    </MsalProvider>
  );
}

export default RootProviders;
